import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { view } from './supabase.js';
import { todayISO, addD } from './util.js';
import { useAuth } from './auth.jsx';

// โหลดข้อมูลหลักทั้งแอพ + helper เช็ควันว่าง (logic เดียวกับ prototype)
const DataCtx = createContext(null);

export function DataProvider({ children }) {
  const { me } = useAuth();
  const [d, setD] = useState({
    catalog: [], units: [], bookings: [], contracts: [], items: [],
    customers: [], credits: [], staff: [], rolePerms: [], roles: [], branches: [],
    resale: [], tradeins: [], dash: null, loaded: false,
  });
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    if (!me) return;
    try {
      setErr(null);
      const [catalog, units, bookings, contracts, customers, credits,
             staff, rolePerms, roles, branches, resale, tradeins, dashRows] = await Promise.all([
        view('rp_v_catalog', q => q.eq('is_active', true).order('category').order('name')),
        view('rp_v_units', q => q.neq('status', 'retired').order('sku').order('unit_code')),
        view('rp_v_bookings', q => q.gte('to_date', addD(todayISO(), -60))),
        view('rp_v_contracts', q => q.order('created_at', { ascending: false }).limit(400)),
        view('rp_v_customers', q => q.order('created_at', { ascending: false })),
        view('rp_v_credits', q => q.order('created_at', { ascending: false })),
        view('rp_v_staff', q => q.order('emp_code')),
        view('rp_v_role_permissions'),
        view('rp_v_roles', q => q.order('rank')),
        view('rp_v_branches'),
        view('rp_v_resale', q => q.order('created_at', { ascending: false })),
        view('rp_v_tradein_intakes', q => q.order('created_at', { ascending: false })),
        view('rp_v_dashboard'),
      ]);
      // รายการอุปกรณ์: ดึงเฉพาะสัญญาที่ยังเปิด (แอพใช้แค่นี้ — Home drawer + หน้ารับคืน)
      // PostgREST ตัดผลลัพธ์ที่ 1000 แถวโดยไม่มี error ถ้าดึงทั้งหมดจะเริ่มหายเงียบตอนสัญญาเกิน ~330 ใบ
      const openIds = contracts.filter(c => c.status === 'open').map(c => c.id);
      const items = openIds.length
        ? await view('rp_v_contract_items', q => q.in('contract_id', openIds))
        : [];
      setD({ catalog, units, bookings, contracts, items, customers, credits,
             staff, rolePerms, roles, branches, resale, tradeins,
             dash: dashRows[0] || null, loaded: true });
    } catch (e) { setErr(e.message); }
  }, [me?.id]);

  useEffect(() => { refresh(); }, [refresh]);

  const helpers = useMemo(() => {
    // ทำดัชนีครั้งเดียวต่อการโหลด — ปฏิทิน 190 ชิ้น × 31 วัน เรียก bookingOn ~5,900 ครั้งต่อการเรนเดอร์
    // ถ้าสแกน array ทั้งก้อนทุกครั้งจะกลายเป็นล้านรอบเทียบ (หน้าค้าง 1-3 วิ ทุกครั้งที่เปลี่ยนเดือน)
    const bkByUnit = new Map();
    for (const b of d.bookings) {
      const a = bkByUnit.get(b.rental_unit_id);
      if (a) a.push(b); else bkByUnit.set(b.rental_unit_id, [b]);
    }
    for (const a of bkByUnit.values()) a.sort((x, y) => x.from_date < y.from_date ? -1 : 1);
    const unitsBySku = new Map();
    for (const u of d.units) {
      const a = unitsBySku.get(u.sku);
      if (a) a.push(u); else unitsBySku.set(u.sku, [u]);
    }
    const EMPTY = [];
    const bkOf = (unitId) => bkByUnit.get(unitId) || EMPTY;

    const bySku = (sku) => d.catalog.find(c => c.sku === sku);
    const unitById = (id) => d.units.find(u => u.id === id);
    const unitsOfSku = (sku) => unitsBySku.get(sku) || EMPTY;
    const bookingsOfUnit = (unitId) => bkOf(unitId);          // เรียงตามวันที่แล้ว — ห้ามแก้ array ในที่
    const bookingOn = (unitId, ds) =>
      bkOf(unitId).find(b => ds >= b.from_date && ds <= b.to_date);
    const isFreeRange = (unitId, from, to) =>
      !bkOf(unitId).some(b => !(to < b.from_date || from > b.to_date));
    const freeUnitsRange = (sku, from, to) =>
      unitsOfSku(sku).filter(u => u.status !== 'retired' && u.status !== 'fix' &&
        isFreeRange(u.id, from, to));
    const nextFree = (unitId, nights) => {
      const t = todayISO();
      for (let i = 0; i < 90; i++) {
        const f = addD(t, i), to = addD(t, i + nights);
        if (isFreeRange(unitId, f, to)) return f;
      }
      return null;
    };
    const openContracts = d.contracts.filter(c => c.status === 'open');
    const itemsOf = (contractId) => d.items.filter(i => i.contract_id === contractId);
    return { bySku, unitById, unitsOfSku, bookingsOfUnit, bookingOn, isFreeRange,
             freeUnitsRange, nextFree, openContracts, itemsOf };
  }, [d]);

  return (
    <DataCtx.Provider value={{ ...d, ...helpers, refresh, err }}>
      {children}
    </DataCtx.Provider>
  );
}

export const useData = () => useContext(DataCtx);
